'use client';

import { useState } from 'react';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { addToFavoriteCourse } from '@eliadmin/core/src/api/jobportal/offers-api';
import { useRouter } from 'next/navigation';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { LuHeart } from 'react-icons/lu';

type AddCourseToFavoritesButtonProps = {
  course: {
    slug: string;
    is_favorite: boolean;
  };
  className?: {
    container?: string;
    icon?: string;
    text?: string;
    button?: string;
    innerContainer?: string;
  };
  showText?: boolean;
};

export function AddCourseToFavoritesButton({
  course,
  className,
  showText,
}: AddCourseToFavoritesButtonProps) {
  const [isFavorite, setIsFavorite] = useState(course?.is_favorite ?? false);
  const { t } = useTranslations();
  const [isLoading, setIsLoading] = useState(false);
  const me = useCustomerStore(state => state.me);
  const router = useRouter();

  const handleSetFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!me) {
      router.push(ROUTES.SIGN_IN());
      return;
    }
    setIsLoading(true);
    addToFavoriteCourse({ authToken: me.authToken, slug: course.slug })
      .then(() => {
        setIsFavorite(prev => !prev);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Button
      variant="default"
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-none bg-white p-0 hover:bg-white/95',
        className?.button,
      )}
      onClick={handleSetFavorite}
      aria-label={isFavorite ? t('text532') : t('text531')}
      aria-pressed={isFavorite}
      disabled={isLoading}
    >
      {isLoading ? (
        <Spinner className="h-5 w-5" />
      ) : (
        <div className={cn('flex flex-row', className?.innerContainer)}>
          <LuHeart
            className={cn(
              'h-5 w-5 stroke-2',
              'text-black',
              isFavorite
                ? 'ea-add-to-favorites__icon--active fill-destructive-foreground stroke-destructive-foreground'
                : 'fill-none',
              className?.icon,
            )}
            aria-hidden="true"
            strokeWidth={1.5}
          />
          {showText && (
            <span className={cn(className?.text)}>{isFavorite ? t('text532') : t('text531')}</span>
          )}
        </div>
      )}
    </Button>
  );
}
